// MediaSelection.js
import React, { useState } from "react";
import styled from "styled-components";
import Tabs from "../../../Tabs";
import Upload from "../../Shared/UploadMedia";
import MediaBrowser from "../MediaBrowser";
import EmbedURL from "../../Shared/EmbedURL";
import EmbedCode from "../EmbedCode";
import RecentMedia from "../RecentMedia";
import { MEDIA_LIBRARY_PICKER_ENABLED } from "../../../../constants/featureFlags";
import { MEDIA_RECORDER_ENABLED } from "../../../../constants/featureFlags";
import { STOCK_IMAGE_LIBRARY_ENABLED } from "../../../../constants/featureFlags";
import MediaRecorder from "../../../MediaCapture";
import MediaLibraryPickerConnected from "../../../MediaLibrary/MediaLibraryPickerConnected";
import StockImageLibrary from "../../../MediaLibrary/StockImageLibrary";
import { isStockEligibleType } from "../../../MediaLibrary/stockImageLibrary.logic";
import { IconLibrary } from "../../../MediaLibrary/icons";
import { acceptedTypesForBuilder, toInsertValue } from "./librarySourceLogic";
import tokens from "../../../../theme/tokens";

// LIB-image-library: the curated preset-image source (flag-gated, image-only).
// Display label "Preset" (renamed from "Stock" 2026-08-18, ISP-9999). STOCK_TAB is
// both the rendered label and the panel-match key, so this one constant renames the
// tab and keeps its StockImageLibrary panel wired.
export const STOCK_TAB = "Preset";

// CAP-A11Y-01 (WCAG 2.4.7): the picker tab strip had NO visible keyboard-focus
// indicator ([role=tab]:focus-visible resolved to outline:0 / box-shadow:none),
// while the adjacent "Reuse from your Media Library" control already shows one.
// Add the app's STANDARD focus ring (design-system token `shadow.focus`,
// 0 0 0 3px #CBB4FE == PrimaryPurple300 — the same ring every MediaLibrary
// control uses) to the picker's tabs. Passed via `sx` so it is SCOPED to this
// picker only (every other Tabs consumer is byte-identical) and ADDITIVE — the
// selector targets ONLY the keyboard-focus states, never the resting/selected
// appearance, and uses a distinct key so it does not clobber the shared Tabs
// component's own `& .MuiTab-root` sizing block. Since this MediaSelection
// picker is injected into ~15 consumer surfaces (Image/Video/Audio/Document
// blocks, Portrait, Wallpaper, …), one additive change fixes them all safely.
const PICKER_TAB_FOCUS_RING_SX = {
  "& .MuiTab-root.Mui-focusVisible, & .MuiTab-root:focus-visible": {
    outline: "none",
    boxShadow: tokens.shadow.focus,
    // keep the ring from being clipped by the MUI Tabs scroller's overflow
    zIndex: 1,
    borderRadius: `${tokens.radius.md}px`,
  },
  // MUI's TabsScroller clips box-shadow (overflow: hidden); reveal the ring.
  // Scoped to THIS picker's Tabs only — the picker's few tabs always fit.
  "& .MuiTabs-scroller": {
    overflow: "visible",
  },
};

// DL-048 (Gordon universal-access addendum 2026-07-14): the "Reuse from your Media
// Library" affordance is mounted HERE, at the shared layer, so every MediaSelection
// consumer (Segment blocks, Portrait, wallpaper/segment-image, Business Box, gallery
// details, …) inherits the reuse modal for free — never a per-surface reimplementation.
// Flag-gated, default OFF: when OFF nothing below renders and the tab strip + panels
// are byte-identical to base.
const ReuseLibraryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 12px;
  padding: 7px 14px;
  min-height: 32px;
  border: 1px solid #9b5af8;
  border-radius: 8px;
  background: #fbfaff;
  color: #6f27d6;
  font: 600 13px "DM Sans", system-ui, sans-serif;
  cursor: pointer;
  transition: background 120ms ease;
  & svg {
    flex: 0 0 auto;
  }
  &:hover {
    background: #efe9fe;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(155, 90, 248, 0.35);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MediaSelection = ({
  src,
  handleChange,
  handleRemove,
  handleTitle,
  type,
  defaultEmbedValue,
  categories = ["Upload", "Embed URL", "Browse", "Recent"],
  isBussinessBox = false,
  media,
  setMedia,
  isGifAllowed = false,
  showRecentDropdown = true,
  showEmbedDisclaimer = false,
  onEmbedURLChange = null,
  // DL-048 modal: consumers MAY declare accepted media types + the open Segment
  // name; by default we derive acceptedTypes from this picker's existing `type`
  // restriction (image/video/audio/document) so no consumer edit is needed.
  acceptedTypes = null,
  segmentName = null,
  // LIB-image-library (NEM F4): opt-out escape hatch. Default TRUE so every image
  // consumer keeps the curated Stock source when the flag is ON — but a surface
  // where curated stock makes no sense (e.g. a legal DMCA takedown/counter form)
  // can pass showStock={false} to suppress it explicitly, without relying only on
  // it happening to pass empty categories. (DMCA forms already pass categories={[]}
  // so they never showed the tab; this makes the intent declarable, not incidental.)
  showStock = true,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Accept exactly what this surface already restricts to (never invent a type).
  const resolvedAcceptedTypes = acceptedTypes ?? acceptedTypesForBuilder(type);

  // LIB-image-library: append the curated "Preset" source ONLY where tabs already
  // show AND this surface accepts images. Flag OFF -> no "Preset" tab, StockImageLibrary
  // never mounts.
  const showStockTab =
    STOCK_IMAGE_LIBRARY_ENABLED &&
    showStock &&
    isStockEligibleType(type) &&
    categories?.length > 0;
  // The Preset source is additive AND ordered: it is inserted immediately BEFORE the
  // "Recent" tab so "Recent" always stays the LAST tab (product order
  // Upload · Embed URL · Browse · Preset · Recent). When the flag is OFF (or categories
  // is empty/undefined) effectiveCategories === caller's categories -> byte-identical.
  const effectiveCategories = (() => {
    if (!categories?.length) return categories;
    if (!showStockTab) return categories;
    const recentIdx = categories.indexOf("Recent");
    if (recentIdx === -1) return [...categories, STOCK_TAB];
    return [
      ...categories.slice(0, recentIdx),
      STOCK_TAB,
      ...categories.slice(recentIdx),
    ];
  })();

  const handleCategoryChange = (event, newValue) => {
    event.stopPropagation();
    setSelectedCategory(newValue);
  };

  return (
    <>
      {effectiveCategories?.length > 0 && (
        <Tabs
          categories={effectiveCategories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          sx={PICKER_TAB_FOCUS_RING_SX}
        />
      )}
      {MEDIA_LIBRARY_PICKER_ENABLED && categories?.length > 0 && (
        <ReuseLibraryBtn
          type="button"
          onClick={() => setPickerOpen(true)}
          data-testid="open-media-library"
        >
          <IconLibrary size={16} /> Reuse from your Media Library
        </ReuseLibraryBtn>
      )}
      {(selectedCategory === categories[0] || categories?.length === 0) && (
        <Upload
          src={src}
          handleMedia={(files) => handleChange(type, files)}
          handleTitle={handleTitle}
          type={type}
          isBusinessBox={isBussinessBox}
          media={media}
          setMedia={setMedia}
        />
      )}
      {selectedCategory === "Embed URL" &&
        type !== "audio" &&
        categories?.length > 1 && (
          <EmbedURL
            defaultValue={defaultEmbedValue}
            type={type}
            onConfirm={(value) => handleChange(type, value)}
            onError={handleRemove}
            pathType={isBussinessBox ? "bussinessBox" : "wheel"}
            media={media}
            showDisclaimer={showEmbedDisclaimer}
            onExternalChange={
              isBussinessBox ? (value) => onEmbedURLChange(value) : null
            }
          />
        )}
      {selectedCategory === "Embed Code" && type === "audio" && (
        <EmbedCode
          onConfirm={(value) => handleChange(type, value)}
          media={media}
        />
      )}
      {MEDIA_RECORDER_ENABLED && selectedCategory === "Record" && (
        <MediaRecorder
          mode={type === "audio" ? "audio" : "video"}
          onComplete={(value) => handleChange(type, value)}
        />
      )}
      {selectedCategory === "Browse" && (
        <MediaBrowser
          mediaType={type}
          onConfirm={(value) => handleChange(type, value)}
          isGifAllowed={isGifAllowed}
        />
      )}
      {selectedCategory === "Recent" && (
        <RecentMedia
          type={type}
          onClickMedia={(value) => handleChange(type, value)}
          showDropdown={showRecentDropdown}
        />
      )}
      {showStockTab && selectedCategory === STOCK_TAB && (
        <StockImageLibrary
          selectedSrc={src}
          onSelect={(value) => handleChange(type, value)}
        />
      )}
      {MEDIA_LIBRARY_PICKER_ENABLED && (
        <MediaLibraryPickerConnected
          open={pickerOpen}
          acceptedTypes={resolvedAcceptedTypes}
          segmentName={segmentName}
          onClose={() => setPickerOpen(false)}
          onInsert={(item) => {
            handleChange(type, toInsertValue(item));
            setPickerOpen(false);
          }}
        />
      )}
    </>
  );
};

export default MediaSelection;
